import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { closeProject, editorSave } from '@/app/editor/core/actions/editorActions';
import { project, store } from '@/app/editor/store';
import { globalStore } from '@/app/globalStore';
const NAVIGATION_IO_IDLE_TIMEOUT_MS = 15_000;
const NAVIGATION_IO_IDLE_POLL_MS = 100;
const NAVIGATION_ROUTE_TRANSITION_TIMEOUT_MS = 15_000;
const LEAVE_EDITOR_NAVIGATION_OVERLAY = 'leaveEditorNavigation';
type LeaveEditorNavigationStore = {
  isNavigating: boolean;
  activeNavigationId: number | null;
  targetPath: string | null;
  fallbackTimeout: ReturnType<typeof setTimeout> | null;
  sequence: number;
};
const leaveEditorNavigationStore: LeaveEditorNavigationStore = {
  isNavigating: false,
  activeNavigationId: null,
  targetPath: null,
  fallbackTimeout: null,
  sequence: 0
};
const leaveEditorNavigationListeners = new Set<() => void>();
function emitLeaveEditorNavigationChange(): void {
  leaveEditorNavigationListeners.forEach(listener => listener());
}
function subscribeLeaveEditorNavigation(listener: () => void): () => void {
  leaveEditorNavigationListeners.add(listener);
  return () => {
    leaveEditorNavigationListeners.delete(listener);
  };
}
function getLeaveEditorNavigationSnapshot(): boolean {
  return leaveEditorNavigationStore.isNavigating;
}
function clearLeaveEditorNavigationOverlay(): void {
  // Hide both slots defensively in case call sites change pinned behaviour.
  globalStore.hideLoadingOverlay(LEAVE_EDITOR_NAVIGATION_OVERLAY, true);
  globalStore.hideLoadingOverlay(LEAVE_EDITOR_NAVIGATION_OVERLAY, false);
}
function finishLeaveEditorNavigation(navigationId?: number): void {
  if (!leaveEditorNavigationStore.isNavigating) {
    clearLeaveEditorNavigationOverlay();
    return;
  }
  if (typeof navigationId === 'number' && leaveEditorNavigationStore.activeNavigationId !== navigationId) {
    return;
  }
  if (leaveEditorNavigationStore.fallbackTimeout) {
    clearTimeout(leaveEditorNavigationStore.fallbackTimeout);
    leaveEditorNavigationStore.fallbackTimeout = null;
  }
  leaveEditorNavigationStore.isNavigating = false;
  leaveEditorNavigationStore.activeNavigationId = null;
  leaveEditorNavigationStore.targetPath = null;
  clearLeaveEditorNavigationOverlay();
  emitLeaveEditorNavigationChange();
}
function beginLeaveEditorNavigation(targetPath: string): number | null {
  if (leaveEditorNavigationStore.isNavigating) {
    return null;
  }
  leaveEditorNavigationStore.sequence += 1;
  const navigationId = leaveEditorNavigationStore.sequence;
  leaveEditorNavigationStore.isNavigating = true;
  leaveEditorNavigationStore.activeNavigationId = navigationId;
  leaveEditorNavigationStore.targetPath = targetPath;
  globalStore.showLoadingOverlay(LEAVE_EDITOR_NAVIGATION_OVERLAY, {
    message: 'Leaving Editor...',
    pinned: true,
    useDimmedBackdrop: true
  });
  emitLeaveEditorNavigationChange();
  return navigationId;
}
function armLeaveEditorNavigationFallback(navigationId: number): void {
  if (leaveEditorNavigationStore.activeNavigationId !== navigationId) {
    return;
  }
  if (leaveEditorNavigationStore.fallbackTimeout) {
    clearTimeout(leaveEditorNavigationStore.fallbackTimeout);
  }
  leaveEditorNavigationStore.fallbackTimeout = setTimeout(() => {
    if (leaveEditorNavigationStore.activeNavigationId !== navigationId) {
      return;
    }
    console.warn('[SafeNavigation] Route transition timed out; releasing leave-editor lock.');
    toast.warning('Navigation took too long; please try again.');
    finishLeaveEditorNavigation(navigationId);
  }, NAVIGATION_ROUTE_TRANSITION_TIMEOUT_MS);
}
async function waitForProjectIoIdle(timeoutMs: number = NAVIGATION_IO_IDLE_TIMEOUT_MS): Promise<boolean> {
  const startTime = Date.now();
  while (store.isSaving || store.isBuildingProject) {
    if (Date.now() - startTime >= timeoutMs) {
      return false;
    }
    await new Promise(resolve => setTimeout(resolve, NAVIGATION_IO_IDLE_POLL_MS));
  }
  return true;
}

/** Hook for safe navigation that checks if we're in editor and automatically saves before navigation */
export const useSafeNavigation = () => {
  const router = useRouter();
  const pathname = usePathname();
  const isNavigating = useSyncExternalStore(subscribeLeaveEditorNavigation, getLeaveEditorNavigationSnapshot, getLeaveEditorNavigationSnapshot);
  const isInEditor = pathname.startsWith('/editor');
  const canSave = isInEditor && store.project.currentScene && project.isProjectOpened;
  useEffect(() => {
    if (!pathname.startsWith('/editor')) {
      // Route transitions can remount this hook, so always clear stale leave-editor overlays on non-editor routes.
      finishLeaveEditorNavigation();
    }
  }, [pathname]);
  const safeNavigate = useCallback(async (targetPath: string) => {
    // If not in editor, navigate directly
    if (!isInEditor) {
      router.push(targetPath);
      return;
    }

    // If navigating within editor, no need to save or tear down FS watch
    if (targetPath.startsWith('/editor')) {
      router.push(targetPath);
      return;
    }
    const navigationId = beginLeaveEditorNavigation(targetPath);
    if (navigationId === null) {
      return;
    }
    let navigationRequested = false;
    try {
      const ioIdleBeforeSave = await waitForProjectIoIdle();
      if (!ioIdleBeforeSave) {
        // Do not block navigation forever if the editor remains busy.
        toast.warning('Project is busy; leaving editor without waiting for save/build idle state.');
      }

      // If no project/scene loaded, navigate directly (already unsubscribed above)
      if (canSave && ioIdleBeforeSave) {
        // Automatically save before leaving editor
        try {
          await editorSave(true, false); // Show success toast but not error toast
        } catch (error_0) {
          console.error('Failed to save before navigation:', error_0);
          toast.error('Failed to save before navigation');
          // Still continue navigation flow even if save fails
        }
      }
      const ioIdleBeforeStop = await waitForProjectIoIdle();
      if (!ioIdleBeforeStop) {
        // Continue with shutdown and navigation to avoid trapping the user in editor route.
        toast.warning('Project is still busy; continuing editor shutdown.');
      }
      try {
        await closeProject();
      } catch (error_1) {
        console.warn('[SafeNavigation] Failed to close project before navigation:', error_1);
      }
      router.push(targetPath);
      navigationRequested = true;
      armLeaveEditorNavigationFallback(navigationId);
    } catch (error) {
      console.warn('[SafeNavigation] Unexpected navigation error:', error);
      finishLeaveEditorNavigation(navigationId);
    } finally {
      if (!navigationRequested) {
        finishLeaveEditorNavigation(navigationId);
      }
    }
  }, [router, isInEditor, canSave]);
  return {
    safeNavigate,
    isNavigating,
    isInEditor,
    canSave
  };
};